/**
 * useResponsivePanelLayout
 * ------------------------
 * Shared layout primitive for the three right-pane / bottom-sheet
 * panels (Drawer, GameplanPanel, ListView). The same component
 * appears on the SIDE of the canvas on desktop / landscape and at
 * the BOTTOM in mobile portrait — this hook owns the geometry so
 * the panels themselves stay focused on their own content and
 * visual styling.
 *
 * Key architectural point: in BOTH orientations the panel is an
 * in-flow flex sibling of the canvas pane, not a `position: fixed`
 * overlay. That guarantees the canvas pane (which uses `flex: 1`)
 * squeezes by exactly the panel's growing dimension — width in
 * landscape, height in portrait — so the 3D scene's ResizeObserver
 * re-runs naturally and the camera stays centered in the visible
 * area. No z-index race, no manual layout math.
 *
 * Returns LAYOUT-ONLY styles (position, dimensions, transitions,
 * anchoring). Each panel applies its own visual styling (background,
 * borders, padding) on top of these.
 */
import type { CSSProperties } from 'react';
import { useAppStore } from '../state/store';
import { useViewport } from './useViewport';

/**
 * Tunables shared by every panel. Keeping them centralized here means
 * a single edit point if we want to revisit the open/close animation
 * feel. The sheet's resting size is no longer constant — it comes
 * from the store (sheetHeight, in px) so the user can drag-resize it
 * via the vertical ResizeHandle on mobile.
 */
export const PANEL_OPEN_MS = 220;
export const PANEL_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';
/** Legacy CSS-string fallback. Used by PanelHandle for its --bjjmap-
 *  sheet-height variable when the panel is closed, so the handle has
 *  a sane resting position even before the hook reads the store. */
export const PANEL_SHEET_HEIGHT_FALLBACK = 'min(50vh, 480px)';

export type ResponsivePanelLayout = {
  /** True when the panel should render as a bottom sheet (mobile portrait). */
  bottomSheet: boolean;
  /** Inline style for the outer <aside> — drives the open/close
   *  animation by transitioning either width (side) or height
   *  (bottom). The outer aside has `overflow: hidden` so the inner
   *  surface's slide-in is clipped during the animation. */
  asideStyle: CSSProperties;
  /** Inline style for the visual surface div nested inside the
   *  <aside>. Positioned absolute and anchored to the edge that
   *  "opens" — right (side) or bottom (sheet) — so it stays its
   *  full open size while the aside grows from 0. */
  innerSurfaceStyle: CSSProperties;
  /** Hint for any consumer needing to know how large the panel is
   *  when open (e.g. close-affordance positioning). */
  openDimension: string;
};

export type ResponsivePanelOpts = {
  /** Whether the panel is currently displayed. Drives the animated
   *  dimension between 0 and the open size. */
  open: boolean;
  /** Side-drawer width from the store. Ignored in bottom-sheet mode. */
  drawerWidth: number;
  /** If true, skip the width transition on the side drawer (used
   *  during an active drag so the handle stays pixel-locked). */
  resizing?: boolean;
  /** Whether to apply the bjjmap-panel-in keyframe on the OUTER
   *  aside. Used by panels that mount/unmount on tab swap so the
   *  fresh-mount reads as a slide-in even though the dimension
   *  itself didn't transition from 0. */
  slideInOnMount?: boolean;
};

/**
 * Compute the layout styles for a side / bottom panel.
 *
 * Implementation notes:
 *   - Outer <aside> is `position: relative` in BOTH modes so it
 *     lives in the App's flex container. That's what lets the
 *     canvas pane squeeze along with us.
 *   - Inner surface is `position: absolute` and anchored to the
 *     "opening" edge so it keeps its full open size while the
 *     aside grows from 0 — producing the slide-in feel without any
 *     transform (flex can still measure the aside's dimension).
 */
export function useResponsivePanelLayout(
  opts: ResponsivePanelOpts,
): ResponsivePanelLayout {
  const { open, drawerWidth, resizing = false, slideInOnMount = false } = opts;
  const { isMobile, isPortrait } = useViewport();
  const bottomSheet = isMobile && isPortrait;
  // Sheet height comes from the store so the user-drag in
  // ResizeHandle persists across opens and panel swaps.
  const sheetHeightPx = useAppStore((s) => s.sheetHeight);

  const openWidth = `min(${drawerWidth}px, 96vw)`;
  const openHeight = `${sheetHeightPx}px`;
  const openDimension = bottomSheet ? openHeight : openWidth;

  const asideStyle: CSSProperties = bottomSheet
    ? {
        // BOTTOM-SHEET (mobile portrait):
        // In-flow flex sibling whose HEIGHT grows from 0 → sheetHeight.
        // App.tsx switches the parent container to flexDirection: column
        // in this mode so this height push pulls the canvas pane up.
        position: 'relative',
        width: '100%',
        flexShrink: 0,
        height: open ? openHeight : 0,
        overflow: 'hidden',
        // Skip the transition while the user is mid-drag on the
        // vertical ResizeHandle so the panel tracks pointer 1:1.
        transition: resizing ? 'none' : `height ${PANEL_OPEN_MS}ms ${PANEL_EASE}`,
        willChange: 'height',
      }
    : {
        // SIDE-DRAWER (desktop / landscape):
        // In-flow flex sibling whose WIDTH grows from 0 → drawerWidth.
        // The canvas pane (flex: 1) shrinks horizontally in lockstep.
        position: 'relative',
        flexShrink: 0,
        width: open ? openWidth : 0,
        height: '100vh',
        overflow: 'hidden',
        transition: resizing
          ? 'none'
          : `width ${PANEL_OPEN_MS}ms ${PANEL_EASE}`,
        willChange: 'width',
        ...(slideInOnMount
          ? {
              animation: `bjjmap-panel-in ${PANEL_OPEN_MS}ms ${PANEL_EASE} both`,
            }
          : {}),
      };

  const innerSurfaceStyle: CSSProperties = bottomSheet
    ? {
        // Pinned to the bottom edge so the open animation reads
        // as "rising from the bottom".
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
      }
    : {
        // Pinned to the RIGHT edge so the slide-in reads as
        // "drawer emerging from the right".
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        width: openWidth,
      };

  return {
    bottomSheet,
    asideStyle,
    innerSurfaceStyle,
    openDimension,
  };
}
